// =============================================================================
// ASK ME WIDGET — Floating AI help chat for the entire platform
//
// Persists across all pages. Sends current page context with each query
// so Claude knows what the user is looking at.
// =============================================================================

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useChatStore, type ChatMessage } from '@/lib/chatStore';

const PLATFORM_URL = import.meta.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const CHAT_API = `${PLATFORM_URL}/functions/v1/ask-me-chat`;

// Map routes to friendly page names
function getPageName(pathname: string): string {
  if (pathname.includes('/manage/plans')) return 'B2C Plans';
  if (pathname.includes('/manage/billing')) return 'Billing';
  if (pathname.includes('/manage/features')) return 'Features';
  if (pathname.includes('/manage/analytics')) return 'Analytics';
  if (pathname.includes('/manage/branding')) return 'Branding';
  if (pathname.includes('/manage/users')) return 'Users';
  if (pathname.includes('/manage/domain')) return 'Custom Domain';
  if (pathname.includes('/manage/email')) return 'Email';
  if (pathname.includes('/manage/settings')) return 'Settings';
  if (pathname.includes('/manage/dashboard') || pathname.includes('/manage')) return 'Dashboard';
  if (pathname.includes('/admin/analytics')) return 'Platform Analytics';
  if (pathname.includes('/admin/tenants')) return 'Tenants';
  if (pathname.includes('/admin/feature-gating')) return 'Feature Gating';
  if (pathname.includes('/admin/plan-builder')) return 'Plan Builder';
  if (pathname.includes('/admin')) return 'Overview';
  return 'Platform';
}

export function AskMeWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const { messages, isOpen, isLoading, pendingPrompt, addMessage, clearMessages, setIsOpen, setIsLoading, setPendingPrompt } = useChatStore();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const pageName = getPageName(location.pathname);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened, and consume any pending prompt set by another
  // page (e.g. the "Write a bio with AI" shortcut on Branding > Splash).
  useEffect(() => {
    if (isOpen) {
      if (pendingPrompt) {
        setInput(pendingPrompt);
        setPendingPrompt(null);
      }
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, pendingPrompt, setPendingPrompt]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      timestamp: Date.now(),
    };
    addMessage(userMsg);
    setInput('');
    setIsLoading(true);

    try {
      const apiMessages = [...messages, userMsg].map(m => ({
        role: m.role,
        content: m.content,
      }));

      const res = await fetch(CHAT_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          messages: apiMessages,
          currentPage: pageName,
        }),
      });

      const data = await res.json();
      const reply = data.reply || data.error || 'Sorry, something went wrong.';

      addMessage({
        id: crypto.randomUUID(),
        role: 'assistant',
        content: reply,
        timestamp: Date.now(),
      });
    } catch {
      addMessage({
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'Unable to reach the help service. Please try again.',
        timestamp: Date.now(),
      });
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, messages, pageName, addMessage, setIsLoading]);

  // Don't render for unauthenticated users (after all hooks)
  if (!user) return null;

  // Hide on Branding (which has its own dedicated BrandingAssistant FAB
  // — two chatbots side-by-side was confusing).
  // /pitch and /vision were public marketing pages that have been removed;
  // their guard previously lived in HIDDEN_ON_PATHS here.
  if (location.pathname.includes('/manage/branding')) return null;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return createPortal(
    <>
      {isOpen ? (
        <div className="fixed bottom-4 right-4 z-[99999] w-[380px] max-w-[calc(100vw-2rem)] animate-scale-in">
          <div className="bg-surface-0 border border-overlay/[0.08] shadow-elevated rounded-2xl flex flex-col overflow-hidden"
            style={{ height: 480 }}>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-overlay/[0.06] bg-brand-500/5">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-brand-500 flex items-center justify-center">
                  <MessageCircle size={14} className="text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-surface-900">Ask Me Anything</h3>
                  <p className="text-[10px] text-surface-500">{pageName}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {messages.length > 0 && (
                  <button onClick={clearMessages}
                    className="p-1.5 text-surface-400 hover:text-surface-600 rounded-lg hover:bg-surface-100 transition-colors"
                    title="Clear chat">
                    <Trash2 size={13} />
                  </button>
                )}
                <button onClick={() => setIsOpen(false)}
                  className="p-1.5 text-surface-400 hover:text-surface-600 rounded-lg hover:bg-surface-100 transition-colors">
                  <X size={15} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 scrollbar-thin">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <div className="w-10 h-10 rounded-full bg-brand-500/10 flex items-center justify-center mx-auto mb-3">
                    <MessageCircle size={20} className="text-brand-500" />
                  </div>
                  <p className="text-sm text-surface-500 mb-1">How can I help?</p>
                  <p className="text-xs text-surface-400">Ask about anything you see on this page.</p>
                  <div className="mt-4 space-y-1.5">
                    {[
                      'What does "Sync" mean?',
                      'How do I accept payments?',
                      'What happens if I cancel?',
                    ].map(q => (
                      <button key={q} onClick={() => { setInput(q); }}
                        className="block w-full text-left text-xs text-brand-600 hover:text-brand-700 bg-brand-500/5 hover:bg-brand-500/10 rounded-lg px-3 py-2 transition-colors">
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map(msg => (
                <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-brand-500 text-white rounded-br-sm'
                      : 'bg-surface-100 text-surface-800 rounded-bl-sm'
                  }`}>
                    {msg.content}
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-surface-100 rounded-xl px-3 py-2 rounded-bl-sm">
                    <Loader2 size={14} className="animate-spin text-surface-400" />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-overlay/[0.06] px-3 py-2.5">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your question..."
                  className="flex-1 bg-surface-100 border border-overlay/[0.06] rounded-xl px-3 py-2 text-sm text-surface-900 placeholder:text-surface-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  disabled={isLoading}
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || isLoading}
                  className="p-2 rounded-xl bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Send size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Floating action button */
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-5 right-5 z-[99999] group flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-brand-500 text-white shadow-elevated hover:shadow-card-hover hover:bg-brand-600 transition-all"
        >
          <MessageCircle size={18} />
          <span className="text-sm font-medium">Ask Me</span>
        </button>
      )}
    </>,
    document.body,
  );
}
